/**
 * Single-path managed-identity list used by the Models tab.
 * Editing is delegated to ManagedIdentityEditor so this entry point cannot
 * drift from the full Identities page.
 */
import React from 'react';
import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Add20Regular, Delete20Regular, Edit20Regular } from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTmdsClient } from '../services/tmds';
import { getErrorMessage } from '../utils/errors';
import {
  type ManagedIdentityEditorRecord,
  toManagedIdentityEditorRecord,
} from '../utils/managedIdentityEditor';
import ManagedIdentityEditor from './ManagedIdentityEditor';

const useStyles = makeStyles({
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalS,
  },
});

export interface IdentityManagerProps {
  apiPath: string;
  client: ReturnType<typeof createTmdsClient>;
  callerEmail?: string;
}

interface EditorState {
  mode: 'create' | 'edit';
  identity: ManagedIdentityEditorRecord | null;
}

export default function IdentityManager({ apiPath, client, callerEmail = '' }: IdentityManagerProps) {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [editor, setEditor] = React.useState<EditorState | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<ManagedIdentityEditorRecord | null>(null);
  const [syncNotice, setSyncNotice] = React.useState(false);

  const query = useQuery({
    queryKey: ['managemsi', apiPath],
    queryFn: () => client.getManagedIdentities({ apiPath }),
    enabled: !!apiPath,
  });

  const items = React.useMemo(
    () => (query.data ?? [])
      .map(record => toManagedIdentityEditorRecord(record, apiPath))
      .filter((record): record is ManagedIdentityEditorRecord => record !== null),
    [apiPath, query.data],
  );

  const deleteIdentity = useMutation({
    mutationFn: async (id: string) => client.deleteMSI({ apiPath, id }),
    onSuccess: async () => {
      setDeleteTarget(null);
      setSyncNotice(true);
      await queryClient.invalidateQueries({ queryKey: ['managemsi', apiPath] });
    },
    onError: (error: unknown) => {
      console.error('Delete identity failed:', getErrorMessage(error));
    },
  });

  if (query.isLoading) return <Spinner size="small" label="Loading identities..." />;
  if (query.isError) return <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{getErrorMessage(query.error)}</Text>;

  return (
    <>
      <div className={styles.headerRow}>
        <Caption1>{items.length} managed {items.length === 1 ? 'identity' : 'identities'}</Caption1>
        <Button
          size="small"
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => setEditor({ mode: 'create', identity: null })}
          disabled={deleteIdentity.isPending}
        >
          Add
        </Button>
      </div>

      {syncNotice && (
        <MessageBar intent="warning" style={{ marginTop: 4, marginBottom: 4 }}>
          <MessageBarBody>
            Changes saved - identity will sync at the next hourly refresh (top of the hour).
          </MessageBarBody>
        </MessageBar>
      )}

      {items.length === 0 ? (
        <Caption1>No managed identities for this API path.</Caption1>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Managed Identities">
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '30%' }}>Name</TableHeaderCell>
                <TableHeaderCell style={{ width: '30%' }}>Owner(s)</TableHeaderCell>
                <TableHeaderCell style={{ width: '15%' }}>Expiry</TableHeaderCell>
                <TableHeaderCell style={{ width: '25%', textAlign: 'right' }}>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={item.id || index}>
                  <TableCell>
                    <Text weight="semibold" size={200} style={{ display: 'block' }}>{item.name || '(unnamed)'}</Text>
                    <Caption1 style={{ fontFamily: 'monospace', color: tokens.colorNeutralForeground3 }} title={item.id}>
                      {item.id ? `${item.id.slice(0, 8)}...` : '-'}
                    </Caption1>
                  </TableCell>
                  <TableCell><Caption1>{item.ownerDisplayTexts.join(', ') || '-'}</Caption1></TableCell>
                  <TableCell><Caption1>{item.endDate || '-'}</Caption1></TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Edit20Regular />}
                        onClick={() => setEditor({ mode: 'edit', identity: item })}
                        disabled={deleteIdentity.isPending}
                        title="Edit"
                        aria-label="Edit identity"
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        appearance="outline"
                        icon={<Delete20Regular />}
                        style={{ color: tokens.colorPaletteRedForeground1, borderColor: tokens.colorPaletteRedForeground1 }}
                        onClick={() => setDeleteTarget(item)}
                        disabled={deleteIdentity.isPending || !item.id}
                        title="Delete"
                        aria-label="Delete identity"
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ManagedIdentityEditor
        open={editor !== null}
        mode={editor?.mode ?? 'create'}
        identity={editor?.identity}
        fixedApiPath={apiPath}
        client={client}
        callerEmail={callerEmail}
        onClose={() => setEditor(null)}
        onSaved={async () => {
          setSyncNotice(true);
          await queryClient.invalidateQueries({ queryKey: ['managemsi', apiPath] });
        }}
      />

      <Dialog open={deleteTarget !== null} onOpenChange={(_, data) => { if (!data.open) setDeleteTarget(null); }}>
        <DialogSurface><DialogBody>
          <DialogTitle>Delete Identity</DialogTitle>
          <DialogContent>
            {deleteIdentity.isError && <Text role="alert" style={{ color: tokens.colorPaletteRedForeground1 }}>{getErrorMessage(deleteIdentity.error)}</Text>}
            <Text>Delete{deleteTarget?.name ? ` "${deleteTarget.name}"` : ''} from <strong>{apiPath}</strong>?</Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setDeleteTarget(null)} disabled={deleteIdentity.isPending}>Cancel</Button>
            <Button
              appearance="primary"
              onClick={() => deleteTarget?.id && deleteIdentity.mutate(deleteTarget.id)}
              disabled={deleteIdentity.isPending || !deleteTarget?.id}
              style={{ color: tokens.colorPaletteRedForeground1 }}
            >
              {deleteIdentity.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </DialogBody></DialogSurface>
      </Dialog>
    </>
  );
}
